import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';

// ============================================================
// Promotion Service — the Star Line Group showcase
//
// First-party promotions only: sibling businesses and Star Line's own bus
// offers, curated by an admin. There is no ad network, no external script and
// no tracking pixel behind any of this.
// ============================================================

// `promotions` is newer than the generated Database types, so the same
// loosely-typed handle the rest of the service layer uses. Runtime object is
// identical; this relaxes compile-time typing only. (See counterService.ts.)
const db = supabase as unknown as SupabaseClient;

function callRpc<T>(fn: string, args?: Record<string, unknown>) {
  return (db.rpc as unknown as (
    f: string, a?: Record<string, unknown>,
  ) => Promise<{ data: T | null; error: { message?: string } | null }>)(fn, args);
}

export type PromotionMediaType = 'image' | 'youtube';
export type PromotionStatus = 'draft' | 'active' | 'archived';

/** A promotion as the public site consumes it, already language-resolved. */
export interface Promotion {
  id: string;
  title: string;
  subtitle: string;
  mediaType: PromotionMediaType;
  imageUrl: string;
  youtubeId: string | null;
  ctaLabel: string;
  ctaUrl: string | null;
}

/** The full row, as the admin panel edits it. */
export interface PromotionRecord {
  id: string;
  titleEn: string;
  titleBn: string;
  subtitleEn: string;
  subtitleBn: string;
  mediaType: PromotionMediaType;
  imageUrl: string;
  youtubeId: string;
  ctaLabelEn: string;
  ctaLabelBn: string;
  ctaUrl: string;
  placement: string;
  sortOrder: number;
  status: PromotionStatus;
  startsAt: string | null;
  endsAt: string | null;
  clickCount: number;
  updatedAt: string | null;
}

interface PromotionRow {
  id: string;
  title_en: string;
  title_bn: string | null;
  subtitle_en: string | null;
  subtitle_bn: string | null;
  media_type: string;
  image_url: string;
  youtube_id: string | null;
  cta_label_en: string | null;
  cta_label_bn: string | null;
  cta_url: string | null;
  placement: string;
  sort_order: number;
  status: string;
  starts_at: string | null;
  ends_at: string | null;
  click_count: number;
  updated_at: string | null;
}

const SELECT_COLUMNS =
  'id, title_en, title_bn, subtitle_en, subtitle_bn, media_type, image_url, youtube_id, ' +
  'cta_label_en, cta_label_bn, cta_url, placement, sort_order, status, starts_at, ends_at, ' +
  'click_count, updated_at';

// ── Artwork spec ─────────────────────────────────────────────
// The showcase is a 4:5 poster rail, one ratio for the whole row — the rule
// every streaming service follows, because mixed shapes in a row never look
// deliberate. 4:5 rather than 16:9 because it matches the artwork Star Line's
// marketing actually produces: portrait posters with the headline at the top
// and the hotline at the bottom, exactly what a wide centre-crop destroys.
//
// The spec is stated at the point of upload and mismatches are flagged there,
// so a bad crop is caught before it is published rather than discovered live.

/** Target artwork ratio: 4:5 portrait poster. */
export const PROMO_ASPECT_RATIO = 4 / 5;
/** Recommended pixel size — 2x the largest rendered card, for retina screens. */
export const PROMO_RECOMMENDED_WIDTH = 1080;
export const PROMO_RECOMMENDED_HEIGHT = 1350;
/** Below this, upscaling shows on a retina display. */
export const PROMO_MIN_WIDTH = 600;
/** How far from 4:5 is tolerable before the crop becomes noticeable. */
const ASPECT_TOLERANCE = 0.12;

/**
 * Ask Supabase Storage for the exact crop this card needs, rendered and cached
 * at the edge.
 *
 * Without this, a 5 MB poster uploaded by an admin is downloaded at full size
 * by every phone on a Bangladeshi mobile connection, then scaled down in the
 * browser. Storage can do the resize for us and cache the result.
 *
 * Any URL that is not a Supabase public object — a static file under
 * /promotions, or an external host — is returned untouched.
 */
export function promoImageUrl(url: string, renderedWidth = 320): string {
  if (!url || !url.includes('/storage/v1/object/public/')) return url;

  // 2x for retina, capped at the source we ask admins to upload.
  const width = Math.min(renderedWidth * 2, PROMO_RECOMMENDED_WIDTH);
  const height = Math.round(width / PROMO_ASPECT_RATIO);
  const base = url.replace('/storage/v1/object/public/', '/storage/v1/render/image/public/');
  const sep = base.includes('?') ? '&' : '?';
  return `${base}${sep}width=${width}&height=${height}&resize=cover&quality=80`;
}

export type ArtworkVerdict =
  | { level: 'ok' }
  | { level: 'warn'; message: string }
  | { level: 'error'; message: string };

/**
 * Judge an uploaded image against the card it has to live in.
 *
 * Returns a warning rather than a hard failure for the wrong ratio: the crop
 * preview sits right beside this, so an operator who is happy with what they
 * see should not be blocked.
 */
export function checkArtwork(width: number, height: number): ArtworkVerdict {
  if (!width || !height) return { level: 'ok' };

  if (width < PROMO_MIN_WIDTH) {
    return {
      level: 'error',
      message: `This image is only ${width}px wide. Use at least ${PROMO_MIN_WIDTH}px — ideally ${PROMO_RECOMMENDED_WIDTH}×${PROMO_RECOMMENDED_HEIGHT} — or it will look soft on the homepage.`,
    };
  }

  const ratio = width / height;
  const drift = Math.abs(ratio - PROMO_ASPECT_RATIO) / PROMO_ASPECT_RATIO;
  if (drift <= ASPECT_TOLERANCE) return { level: 'ok' };

  if (ratio < PROMO_ASPECT_RATIO) {
    // Taller than 4:5 — the crop takes the top and bottom.
    const trimmed = Math.round((1 - (width / PROMO_ASPECT_RATIO) / height) * 100);
    return {
      level: 'warn',
      message: `This is ${width}×${height}, taller than the 4:5 poster card. About ${trimmed}% of the height will be cropped — usually the headline at the top or the hotline at the bottom. Check the preview, or re-export at ${PROMO_RECOMMENDED_WIDTH}×${PROMO_RECOMMENDED_HEIGHT}.`,
    };
  }

  const trimmed = Math.round((1 - (height * PROMO_ASPECT_RATIO) / width) * 100);
  return {
    level: 'warn',
    message: `This is ${width}×${height}, wider than the 4:5 poster card. About ${trimmed}% of the sides will be cropped. Check the preview, or re-export at ${PROMO_RECOMMENDED_WIDTH}×${PROMO_RECOMMENDED_HEIGHT} (portrait).`,
  };
}

/**
 * A CTA URL is admin-entered text that becomes an `href` on the public
 * homepage. The database rejects anything that is not http(s) or site-relative,
 * but the check is repeated here so a row written before that constraint
 * existed — or by any future path that bypasses it — still cannot put
 * `javascript:` into the DOM.
 */
export function isSafeCtaUrl(url: string | null | undefined): boolean {
  if (!url) return false;
  const trimmed = url.trim();
  if (trimmed.startsWith('/')) return !trimmed.startsWith('//');
  return /^https?:\/\//i.test(trimmed);
}

/**
 * YouTube ids are a fixed alphabet. Validating rather than trusting keeps the
 * value from breaking out of the embed URL it gets interpolated into.
 */
export function isValidYoutubeId(id: string | null | undefined): boolean {
  return !!id && /^[A-Za-z0-9_-]{6,20}$/.test(id.trim());
}

/**
 * Accepts either a bare id or any of the usual YouTube URL shapes, so an admin
 * can paste whatever the browser gave them.
 */
export function extractYoutubeId(input: string): string {
  const raw = (input || '').trim();
  if (!raw) return '';
  if (isValidYoutubeId(raw) && !raw.includes('/')) return raw;
  const patterns = [
    /[?&]v=([A-Za-z0-9_-]{6,20})/,
    /youtu\.be\/([A-Za-z0-9_-]{6,20})/,
    /youtube\.com\/embed\/([A-Za-z0-9_-]{6,20})/,
    /youtube\.com\/shorts\/([A-Za-z0-9_-]{6,20})/,
  ];
  for (const p of patterns) {
    const m = raw.match(p);
    if (m) return m[1];
  }
  return '';
}

function resolveLang(en: string | null, bn: string | null, lang: string): string {
  // Bangla falls back to English rather than rendering an empty card — a
  // half-translated promo is better than a blank one.
  if (lang === 'bn') return (bn || '').trim() || (en || '').trim();
  return (en || '').trim();
}

/**
 * Live promotions for a placement, resolved into one language.
 *
 * RLS already restricts anonymous reads to rows that are `active` and inside
 * their scheduling window, so this cannot surface a draft even if asked.
 */
export async function getLivePromotions(
  lang: string,
  placement = 'home_showcase',
): Promise<Promotion[]> {
  const { data, error } = await db
    .from('promotions')
    .select(SELECT_COLUMNS)
    .eq('placement', placement)
    .order('sort_order', { ascending: true })
    .order('created_at', { ascending: false });

  if (error || !data) return [];

  return (data as unknown as PromotionRow[])
    .filter(r => r.media_type !== 'youtube' || isValidYoutubeId(r.youtube_id))
    .map(r => ({
      id: r.id,
      title: resolveLang(r.title_en, r.title_bn, lang),
      subtitle: resolveLang(r.subtitle_en, r.subtitle_bn, lang),
      mediaType: r.media_type === 'youtube' ? 'youtube' : 'image',
      imageUrl: r.image_url,
      youtubeId: isValidYoutubeId(r.youtube_id) ? (r.youtube_id as string).trim() : null,
      ctaLabel: resolveLang(r.cta_label_en, r.cta_label_bn, lang),
      ctaUrl: isSafeCtaUrl(r.cta_url) ? (r.cta_url as string).trim() : null,
    }));
}

function toRecord(r: PromotionRow): PromotionRecord {
  return {
    id: r.id,
    titleEn: r.title_en || '',
    titleBn: r.title_bn || '',
    subtitleEn: r.subtitle_en || '',
    subtitleBn: r.subtitle_bn || '',
    mediaType: r.media_type === 'youtube' ? 'youtube' : 'image',
    imageUrl: r.image_url || '',
    youtubeId: r.youtube_id || '',
    ctaLabelEn: r.cta_label_en || '',
    ctaLabelBn: r.cta_label_bn || '',
    ctaUrl: r.cta_url || '',
    placement: r.placement,
    sortOrder: r.sort_order,
    status: (['draft', 'active', 'archived'] as const).includes(r.status as PromotionStatus)
      ? (r.status as PromotionStatus)
      : 'draft',
    startsAt: r.starts_at,
    endsAt: r.ends_at,
    clickCount: r.click_count ?? 0,
    updatedAt: r.updated_at,
  };
}

/** Every promotion, for the admin list. RLS restricts this to admins. */
export async function listPromotions(): Promise<PromotionRecord[]> {
  const { data, error } = await db
    .from('promotions')
    .select(SELECT_COLUMNS)
    .order('sort_order', { ascending: true })
    .order('created_at', { ascending: false });

  if (error || !data) return [];
  return (data as unknown as PromotionRow[]).map(toRecord);
}

export interface PromotionInput {
  titleEn: string;
  titleBn?: string;
  subtitleEn?: string;
  subtitleBn?: string;
  mediaType: PromotionMediaType;
  imageUrl: string;
  youtubeId?: string;
  ctaLabelEn?: string;
  ctaLabelBn?: string;
  ctaUrl?: string;
  placement?: string;
  sortOrder?: number;
  status?: PromotionStatus;
  startsAt?: string | null;
  endsAt?: string | null;
}

/** Shared validation, so the admin form and the service cannot disagree. */
export function validatePromotion(input: PromotionInput): string | null {
  if (!input.titleEn?.trim()) return 'An English title is required.';
  if (!input.imageUrl?.trim()) {
    return input.mediaType === 'youtube'
      ? 'A poster image is required — it is what shows before the video is played.'
      : 'An image is required.';
  }
  if (input.mediaType === 'youtube' && !isValidYoutubeId(input.youtubeId)) {
    return 'Enter a valid YouTube link or video ID.';
  }
  if (input.ctaUrl?.trim() && !isSafeCtaUrl(input.ctaUrl)) {
    return 'The button link must start with https:// or / — other schemes are not allowed.';
  }
  if (input.ctaUrl?.trim() && !input.ctaLabelEn?.trim()) {
    return 'A button link needs a button label.';
  }
  if (input.startsAt && input.endsAt && new Date(input.endsAt) <= new Date(input.startsAt)) {
    return 'The end date must be after the start date.';
  }
  return null;
}

function toRowPayload(input: PromotionInput) {
  return {
    title_en: input.titleEn.trim(),
    title_bn: input.titleBn?.trim() || null,
    subtitle_en: input.subtitleEn?.trim() || null,
    subtitle_bn: input.subtitleBn?.trim() || null,
    media_type: input.mediaType,
    image_url: input.imageUrl.trim(),
    youtube_id: input.mediaType === 'youtube' ? (input.youtubeId || '').trim() : null,
    cta_label_en: input.ctaLabelEn?.trim() || null,
    cta_label_bn: input.ctaLabelBn?.trim() || null,
    cta_url: input.ctaUrl?.trim() || null,
    placement: input.placement || 'home_showcase',
    sort_order: input.sortOrder ?? 0,
    status: input.status || 'draft',
    starts_at: input.startsAt || null,
    ends_at: input.endsAt || null,
  };
}

export async function createPromotion(
  input: PromotionInput,
): Promise<{ id?: string; error?: string }> {
  const invalid = validatePromotion(input);
  if (invalid) return { error: invalid };

  const { data, error } = await db
    .from('promotions')
    .insert(toRowPayload(input))
    .select('id')
    .single();

  if (error) return { error: error.message || 'Could not save this promotion.' };
  return { id: (data as { id: string }).id };
}

export async function updatePromotion(
  id: string,
  input: PromotionInput,
): Promise<{ error?: string }> {
  const invalid = validatePromotion(input);
  if (invalid) return { error: invalid };

  const { error } = await db.from('promotions').update(toRowPayload(input)).eq('id', id);
  if (error) return { error: error.message || 'Could not save this promotion.' };
  return {};
}

export async function setPromotionStatus(
  id: string,
  status: PromotionStatus,
): Promise<{ error?: string }> {
  const { error } = await db.from('promotions').update({ status }).eq('id', id);
  if (error) return { error: error.message || 'Could not change the status.' };
  return {};
}

export async function deletePromotion(id: string): Promise<{ error?: string }> {
  const { error } = await db.from('promotions').delete().eq('id', id);
  if (error) return { error: error.message || 'Could not delete this promotion.' };
  return {};
}

/**
 * Count a click. Best-effort by design: a failed count must never stop the
 * customer reaching the page they asked for, so this swallows its errors.
 */
export async function recordPromotionClick(id: string): Promise<void> {
  try {
    await callRpc<null>('record_promotion_click', { p_promotion_id: id });
  } catch {
    /* analytics is not worth a broken link */
  }
}

/** Upload artwork to the admin-only bucket and return its public URL. */
export async function uploadPromotionImage(
  file: File,
): Promise<{ url?: string; error?: string }> {
  const ext = (file.name.split('.').pop() || 'jpg').toLowerCase().replace(/[^a-z0-9]/g, '');
  const path = `${crypto.randomUUID()}.${ext || 'jpg'}`;

  const { error } = await supabase.storage
    .from('promotions')
    .upload(path, file, { cacheControl: '31536000', upsert: false });

  if (error) return { error: error.message || 'Upload failed.' };

  const { data } = supabase.storage.from('promotions').getPublicUrl(path);
  return { url: data.publicUrl };
}
